import type {UnknownAction} from '@reduxjs/toolkit'
import {trackEvent, trackEventDebounced} from './trackEvent'
import type {AnalyticsEventName} from './events'

// Post-hoc dispatch wrapper that maps `scenesSlice` actions to GA events.
// Installed via installGaDispatchWrapper(store) right after configureStore — we don't use
// configureStore's `middleware` option because it widens RootState inference to `any`
// in RTK 2.x.
//
// Mergeable (continuous-control) actions go through trackEventDebounced so a slider drag
// produces one event with the final value, not dozens. Discrete actions fire immediately.

interface Mapping {
  readonly name: AnalyticsEventName
  readonly buildParams?: (action: UnknownAction) => Record<string, unknown>
  readonly debounced?: boolean
}

const payloadValue = (action: UnknownAction): unknown => (action as {payload?: unknown}).payload

const numericPayload = (action: UnknownAction): Record<string, unknown> => {
  const value = payloadValue(action)
  return {value: typeof value === 'number' ? Number(value.toFixed(3)) : value}
}

const materialPartFromType = (type: string): {part: string; prop: string} | null => {
  const match =
    /^scenes\/set([A-Z][A-Za-z]*?)(Color|Metalness|Roughness|Clearcoat|ClearcoatRoughness|Reflectivity|NoiseIntensity|NormalStrength)$/.exec(
      type,
    )
  if (!match) return null
  const part = match[1].replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
  const prop = match[2].replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
  return {part, prop}
}

const STATIC_MAPPING: Record<string, Mapping> = {
  'scenes/setDeviceWidth': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'width', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDeviceHeight': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'height', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDeviceDepth': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'depth', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDeviceCornerRadius': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'corner_radius', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDeviceEdgeBevel': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'edge_bevel', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDeviceRotationZ': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'rotation_z', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDevicePositionZ': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'position_z', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setFrontFaceInset': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'front_face_inset', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDisplayCornerRadius': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'display_corner_radius', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDisplayInsetX': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'display_inset_x', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDisplayInsetY': {
    name: 'device_geometry_changed',
    buildParams: (a) => ({prop: 'display_inset_y', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setDisplayReflection': {
    name: 'material_changed',
    buildParams: (a) => ({part: 'display', prop: 'reflection', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setBgMode': {name: 'background_changed', buildParams: (a) => ({prop: 'mode', value: payloadValue(a)})},
  'scenes/setBgImageUrl': {name: 'background_changed', buildParams: () => ({prop: 'image_url'})},
  'scenes/addGradientStop': {name: 'background_changed', buildParams: () => ({prop: 'gradient_stop_added'})},
  'scenes/removeGradientStop': {name: 'background_changed', buildParams: () => ({prop: 'gradient_stop_removed'})},
  'scenes/updateGradientStop': {
    name: 'background_changed',
    buildParams: () => ({prop: 'gradient_stop_updated'}),
    debounced: true,
  },
  'scenes/setBgAngle': {
    name: 'background_changed',
    buildParams: (a) => ({prop: 'gradient_angle', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setEnvPreset': {
    name: 'lighting_changed',
    buildParams: (a) => ({prop: 'env_preset', value: payloadValue(a)}),
  },
  'scenes/setEnvMapIntensity': {
    name: 'lighting_changed',
    buildParams: (a) => ({prop: 'env_intensity', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setAmbientIntensity': {
    name: 'lighting_changed',
    buildParams: (a) => ({prop: 'ambient_intensity', ...numericPayload(a)}),
    debounced: true,
  },
  'scenes/setAmbientColor': {name: 'lighting_changed', buildParams: () => ({prop: 'ambient_color'}), debounced: true},
  'scenes/updateLight': {name: 'lighting_changed', buildParams: () => ({prop: 'light_updated'}), debounced: true},
  'scenes/addScene': {name: 'scene_added'},
  'scenes/addNewScene': {name: 'scene_added'},
  'scenes/removeScene': {name: 'scene_removed'},
  'scenes/renameScene': {name: 'scene_renamed'},
  'scenes/setActiveScene': {name: 'scene_switched'},
  'scenes/setExportFrameEnabled': {
    name: 'export_options_changed',
    buildParams: (a) => ({prop: 'frame_enabled', value: payloadValue(a)}),
  },
  'scenes/setExportCarouselEnabled': {
    name: 'export_options_changed',
    buildParams: (a) => ({prop: 'carousel_enabled', value: payloadValue(a)}),
  },
  'scenes/setExportCarouselCount': {
    name: 'export_options_changed',
    buildParams: (a) => ({prop: 'carousel_count', value: payloadValue(a)}),
  },
  'scenes/setExportSize': {
    name: 'export_options_changed',
    buildParams: (a) => ({prop: 'size', value: payloadValue(a)}),
  },
}

const resolveMapping = (type: string): Mapping | null => {
  const direct = STATIC_MAPPING[type]
  if (direct) return direct
  const material = materialPartFromType(type)
  if (material) {
    return {
      name: 'material_changed',
      buildParams: (action) => ({part: material.part, prop: material.prop, ...numericPayload(action)}),
      debounced: true,
    }
  }
  return null
}

const handleAction = (action: unknown): void => {
  if (typeof action !== 'object' || action === null || !('type' in action)) return
  const type = (action as UnknownAction).type
  if (typeof type !== 'string') return
  const mapping = resolveMapping(type)
  if (!mapping) return
  const params = mapping.buildParams ? mapping.buildParams(action as UnknownAction) : undefined
  if (mapping.debounced) {
    trackEventDebounced(type, mapping.name, params)
  } else {
    trackEvent(mapping.name, params)
  }
}

interface StoreLike {
  dispatch: (action: unknown) => unknown
}

export const installGaDispatchWrapper = (store: StoreLike): void => {
  const originalDispatch = store.dispatch.bind(store)
  store.dispatch = (action: unknown): unknown => {
    const result = originalDispatch(action)
    handleAction(action)
    return result
  }
}
